import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import {
  PapiDaSharedOriginalsOrder,
  PapiOriginalsResponse,
  PapiDeviation,
} from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import OrderSummary from '@wix/da-ds/pkg/originals/OrderSummary';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { usePushModal } from '@wix/da-shared-react/pkg/utils/hooks/usePushModal';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import QuestionnaireAnswers from './_partials/QuestionnaireAnswers';
import {
  declineOriginalOrder,
  acceptOriginalOrder,
  fetchOriginalsOrderDetails,
} from '../../actions/studio/originals';

import s from './OriginalsOrderDetailsModal.scss';

interface Props {
  deviationId: number;
  responseId: number;
  onRequestClose: () => void;
}

const OriginalsOrderDetailsModal: React.FC<Props> = ({
  deviationId,
  responseId,
  onRequestClose,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const [deviation, setDeviation] = useState<PapiDeviation | null>(null);
  const [order, setOrder] = useState<PapiOriginalsResponse | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(true);

  const pushOriginalsDeclineModal = usePushModal(
    ModalType.STUDIO_ORIGINALS_DECLINE
  );
  const pushOriginalsDeclineSuccessModal = usePushModal(
    ModalType.STUDIO_ORIGINALS_DECLINE_SUCCESS
  );
  const pushOriginalsAcceptSuccessModal = usePushModal(
    ModalType.STUDIO_ORIGINALS_ACCEPT_SUCCESS
  );

  const summaryProps = useMemo(() => {
    if (!order || !deviation) {
      return undefined;
    }

    const { basePrice = 0, questionnaire = [] } =
      deviation.extended?.originals ?? {};
    const orderAnswers = order.orderAnswers ?? [];
    const selectedAddons = orderAnswers.reduce(
      (acc, { questionId, answerIds }) => {
        const question = questionnaire.find(q => q.questionId === questionId);
        const selectedAnswers =
          question?.answers?.filter(a =>
            answerIds?.includes(a.answerId ?? 0)
          ) ?? [];
        if (selectedAnswers.length > 0) {
          return acc.concat(selectedAnswers);
        }
        return acc;
      },
      []
    );

    return { basePrice, selectedAddons };
  }, [order, deviation]);

  const onDecline = useCallback(
    (reason: string, onSuccess: () => void, onError: () => void) => {
      dispatch(
        declineOriginalOrder({
          deviationId,
          responseId,
          reason,
          onSuccess: () => {
            onSuccess();
            pushOriginalsDeclineSuccessModal({
              requestingUser: order?.user,
            });
            onRequestClose();
          },
          onError: () => {
            onError();
            dispatch(addErrorNotification(t('common.somethingWentWrong')));
            onRequestClose();
          },
        })
      );
    },
    [
      dispatch,
      deviationId,
      responseId,
      order?.user,
      onRequestClose,
      t,
      pushOriginalsDeclineSuccessModal,
    ]
  );

  const onDeclineClick = useCallback(() => {
    pushOriginalsDeclineModal({
      requestingUser: order?.user,
      onDecline,
    });
  }, [pushOriginalsDeclineModal, onDecline, order?.user]);

  const onAcceptClick = useCallback(() => {
    setIsBusy(true);
    dispatch(
      acceptOriginalOrder({
        deviationId,
        responseId,
        onSuccess: () => {
          pushOriginalsAcceptSuccessModal({
            requestingUser: order?.user,
          });
          onRequestClose();
        },
        onError: () => {
          dispatch(addErrorNotification(t('common.somethingWentWrong')));
          onRequestClose();
        },
      })
    );
  }, [
    dispatch,
    deviationId,
    responseId,
    order?.user,
    pushOriginalsAcceptSuccessModal,
    onRequestClose,
    t,
  ]);

  const showActionButtons = useMemo(() => order?.status === 'pending', [order]);

  useEffect(() => {
    dispatch(
      fetchOriginalsOrderDetails({
        deviationId,
        responseId,
        onSuccess: (data: PapiDaSharedOriginalsOrder) => {
          setIsBusy(false);
          setOrder(data.response);
          setDeviation(data.deviation);
        },
        onError: () => {
          dispatch(addErrorNotification(t('common.somethingWentWrong')));
          onRequestClose();
        },
      })
    );
  }, []);

  return (
    <Modal
      className={s['root']}
      layoutClassName={s['layout']}
      contentClassName={s['content']}
      heading={t('studio.modals.orderDetails.title')}
      footer={
        showActionButtons && (
          <div className={s['footer']}>
            <TextButton
              key="decline"
              className={cn(s['button'], s['button-decline'])}
              onClick={onDeclineClick}
              size="medium"
              variant="main"
              disabled={isBusy}
            >
              {t('studio.modals.orderDetails.actions.decline')}
            </TextButton>
            <BrandedButton
              key="accept"
              className={s['button']}
              onClick={onAcceptClick}
              size="medium"
              variant="brand-green"
              disabled={isBusy}
            >
              {t('studio.modals.orderDetails.actions.accept')}
            </BrandedButton>
          </div>
        )
      }
      onRequestClose={onRequestClose}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick={false}
      isOpen
      isFullscreen={isMobile}
      withScrollableContentWrapper
    >
      {order && deviation && (
        <>
          <QuestionnaireAnswers deviation={deviation} order={order} />
          <OrderSummary {...summaryProps} />
        </>
      )}
      {order === null && <LoadingIndicator view="llama" />}
    </Modal>
  );
};

export default OriginalsOrderDetailsModal;
